import { motion } from "motion/react";
import React from "react";
import { AnalyticsEvent } from "../../analytics/track";
import { useAnalytics } from "../../hooks/analytics";
import { useChatUrl } from "../../hooks/chat_url";
import { useResultsOrder } from "../../hooks/results_order";
import { QuestionsIcon } from "../../icons";
import { isExactNear } from "../../lib/routing";
import { useConfig } from "../../providers/config_provider";
import { useSearchQuery } from "../../providers/search_query_provider";
import type { ConversationTurnRouting, Question } from "../../types";
import SearchQuestion from "../content/question";
import SearchSection from "../section";
import type { SearchResultsSectionOrder } from "./data";

type SearchResultsQuestionsProps = {
  questions?: Question[];
  routing?: ConversationTurnRouting;
  order: SearchResultsSectionOrder;
};

export function SearchResultsQuestions({ questions, routing, order }: SearchResultsQuestionsProps) {
  const { organization } = useConfig();
  const { strings } = organization;
  const { questionResultsHeader } = strings;
  const { normalizedQuery } = useSearchQuery();
  const { animationVariants, className } = useResultsOrder(order);
  const followUpUrl = useChatUrl(normalizedQuery);
  const { track } = useAnalytics();

  // Don't show the questions section if there are no questions
  if (!questions?.length) return null;

  // Display parameters for this section based on routing
  const isExactNearRouting = isExactNear(routing);
  const icon = isExactNearRouting ? undefined : QuestionsIcon;
  const title = isExactNearRouting ? undefined : questionResultsHeader;
  const subtitle = isExactNearRouting ? "You may also want to ask" : undefined;

  // Show the follow up button for Near and Exact responses
  const followUpText = "Ask a follow up";
  const followUpButtonDetails = isExactNearRouting
    ? {
        text: followUpText,
        href: followUpUrl,
        icon: QuestionsIcon,
        onClick: () => {
          track(AnalyticsEvent.SEARCH_RESULTS_CLICKED, {
            query: normalizedQuery,
            query_type: routing,
            result_value: {
              rank: -1,
              text: followUpText,
              href: followUpUrl,
            },
            result_type: "interaction",
          });
        },
      }
    : undefined;

  return (
    <motion.div
      key="data--questions"
      variants={animationVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      className={className}
    >
      <SearchSection
        icon={icon}
        title={title}
        subtitle={subtitle}
        meta="questions"
        button={followUpButtonDetails}
      >
        {questions?.map((question) => (
          <SearchQuestion
            key={question.text}
            question={question}
          />
        ))}
      </SearchSection>
    </motion.div>
  );
}
